/**
 * Audit trail entries for item_relationship_edges (Canon 07).
 * Logged on both endpoints so Activity → History shows the change per item.
 */

import { useAuditStore } from '@/stores/prioritization/auditStore';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import type { ItemRelationshipEdge, RelationshipEdgeType } from '@/types/prioritization/itemRelationships';
import { carriesSentence } from '@/lib/carriesRelationships';

const EDGE_TYPE_LABELS: Record<RelationshipEdgeType, string> = {
  related: 'Related',
  dependency: 'Dependency',
  prerequisite: 'Prerequisite',
  aggregation: 'Aggregation',
  elaboration: 'Elaboration',
  breakdown: 'Breakdown',
  contains: 'Contains',
  reframe: 'Additional category',
  carries: 'Carries',
};

function itemTitle(itemId: string): string {
  return useItemsStore.getState().getItemById(itemId)?.title ?? itemId.slice(0, 8);
}

/** How an edge reads from one end ("Depends on", "Part of"). Also used by the item page's Connections rows. */
export function edgeRoleLabel(
  edgeType: RelationshipEdgeType,
  isSource: boolean,
): string {
  switch (edgeType) {
    case 'contains':
      return isSource ? 'Contains' : 'Contained by';
    case 'dependency':
      return isSource ? 'Depends on' : 'Required by';
    case 'prerequisite':
      return isSource ? 'Prerequisite for' : 'Requires';
    case 'aggregation':
      return isSource ? 'Aggregates' : 'Aggregated by';
    case 'breakdown':
      return isSource ? 'Breaks down into' : 'Part of';
    case 'elaboration':
      return isSource ? 'Elaborates' : 'Elaborated by';
    case 'carries':
      return isSource ? 'Carries' : 'Carried by';
    default:
      return isSource ? 'Linked to' : 'Linked from';
  }
}

export function logRelationshipEdgeCreated(edge: ItemRelationshipEdge): void {
  const { logEvent } = useAuditStore.getState();
  const typeLabel = EDGE_TYPE_LABELS[edge.type] ?? edge.type;

  for (const itemId of [edge.sourceItemId, edge.targetItemId]) {
    const isSource = itemId === edge.sourceItemId;
    const otherId = isSource ? edge.targetItemId : edge.sourceItemId;
    const role = edgeRoleLabel(edge.type, isSource);
    const otherTitle = itemTitle(otherId);

    logEvent({
      type: 'relationship_edge_added',
      itemId,
      // A Carries line reads as its sentence: Carries 40% of "X" / "X" carries 40% of this.
      note: edge.type === 'carries' ? carriesSentence(edge, itemId, otherTitle) : `${role}: "${otherTitle}"`,
      payload: {
        edgeId: edge.id,
        edgeType: edge.type,
        edgeTypeLabel: typeLabel,
        role,
        otherItemId: otherId,
        otherItemTitle: otherTitle,
        direction: isSource ? 'source' : 'target',
        rationale: edge.rationale ?? null,
        subtype: edge.subtype ?? null,
        magnitude: edge.magnitude ?? null,
      },
    });
  }
}

export function logRelationshipEdgeDissolved(edge: ItemRelationshipEdge): void {
  const { logEvent } = useAuditStore.getState();
  const typeLabel = EDGE_TYPE_LABELS[edge.type] ?? edge.type;

  for (const itemId of [edge.sourceItemId, edge.targetItemId]) {
    const isSource = itemId === edge.sourceItemId;
    const otherId = isSource ? edge.targetItemId : edge.sourceItemId;
    const otherTitle = itemTitle(otherId);

    logEvent({
      type: 'relationship_edge_dissolved',
      itemId,
      note: `${typeLabel} dissolved: "${otherTitle}"`,
      payload: {
        edgeId: edge.id,
        edgeType: edge.type,
        otherItemId: otherId,
        otherItemTitle: otherTitle,
      },
    });
  }
}
